// SuperAdminCreatePage — /super/admins/new.
//
// Provisions a new platform-admin account via POST /super/admins. The
// server enforces uniqueness on `username` and `email` and returns 409
// AdminConflictError with a `field` discriminator. We inspect that
// payload exactly like SuperCompanyCreatePage does and flag the relevant
// Input. Anything else surfaces in a generalError banner.

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import type { AxiosError } from 'axios';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Checkbox } from '../../components/form/Checkbox';
import {
  useCreateAdmin,
  type CreateAdminInput,
} from './hooks/useSuper';
import styles from './SuperAdminCreatePage.module.css';

const MIN_PASSWORD_LENGTH = 12;

type FieldKey = 'username' | 'email' | 'full_name' | 'password' | 'confirm_password';

interface FormState {
  username: string;
  email: string;
  full_name: string;
  password: string;
  confirm_password: string;
  is_super_admin: boolean;
}

const EMPTY: FormState = {
  username: '',
  email: '',
  full_name: '',
  password: '',
  confirm_password: '',
  is_super_admin: false,
};

interface ServerErrorBody {
  error?: string;
  code?: string;
  message?: string;
  field?: 'username' | 'email';
}

function extractServerError(err: unknown): ServerErrorBody | null {
  const axiosErr = err as AxiosError<ServerErrorBody>;
  if (axiosErr?.response?.data && typeof axiosErr.response.data === 'object') {
    return axiosErr.response.data;
  }
  return null;
}

export function SuperAdminCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [clientErrors, setClientErrors] = useState<
    Partial<Record<FieldKey, string>>
  >({});
  const create = useCreateAdmin();

  const serverError = extractServerError(create.error);
  const serverFieldError: Partial<Record<FieldKey, string>> = {};
  if (serverError?.field && serverError.message) {
    serverFieldError[serverError.field] = serverError.message;
  }
  // Field-level errors prefer the client check first, then any server field.
  const fieldErrors: Partial<Record<FieldKey, string>> = {
    ...serverFieldError,
    ...clientErrors,
  };

  const generalError =
    create.isError && !serverError?.field
      ? serverError?.message ?? 'Failed to create admin.'
      : null;

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    // Clearing the client error on the corresponding field when the user
    // edits keeps the UX in line with the other Super pages.
    if (key in clientErrors) {
      setClientErrors((prev) => {
        const next = { ...prev };
        delete next[key as FieldKey];
        return next;
      });
    }
  }

  function validate(): Partial<Record<FieldKey, string>> {
    const errors: Partial<Record<FieldKey, string>> = {};
    if (form.username.trim() === '') {
      errors.username = 'Username is required.';
    }
    if (form.email.trim() === '') {
      errors.email = 'Email is required.';
    }
    if (form.full_name.trim() === '') {
      errors.full_name = 'Full name is required.';
    }
    if (form.password.length === 0) {
      errors.password = 'Password is required.';
    } else if (form.password.length < MIN_PASSWORD_LENGTH) {
      errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (form.confirm_password.length === 0) {
      errors.confirm_password = 'Please confirm the password.';
    } else if (form.confirm_password !== form.password) {
      errors.confirm_password = 'Passwords do not match.';
    }
    return errors;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setClientErrors(errors);
      return;
    }
    setClientErrors({});

    const payload: CreateAdminInput = {
      username: form.username.trim(),
      email: form.email.trim(),
      full_name: form.full_name.trim(),
      password: form.password,
      is_super_admin: form.is_super_admin,
    };

    try {
      await create.mutateAsync(payload);
      navigate('/super/admins');
    } catch {
      // useCreateAdmin surfaces toasts; field-level handling is driven by
      // the mutation's error state (see fieldErrors / generalError above).
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="New Admin"
        subtitle="Provision a new platform-admin account"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/super/admins')}
          >
            Back
          </Button>
        }
      />

      {generalError ? (
        <Card>
          <p className={styles.generalError} role="alert">
            {generalError}
          </p>
        </Card>
      ) : null}

      <form onSubmit={handleSubmit} className={styles.form}>
        <Card title="New Platform Admin">
          <div className={styles.fields}>
            <Input
              label="Username"
              value={form.username}
              onChange={(e) => setField('username', e.target.value)}
              required
              helper="Letters, digits, underscores only"
              error={
                fieldErrors.username !== undefined
                  ? { message: fieldErrors.username }
                  : undefined
              }
            />
            <Input
              label="Email Address"
              variant="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              required
              error={
                fieldErrors.email !== undefined
                  ? { message: fieldErrors.email }
                  : undefined
              }
            />
            <Input
              label="Full Name"
              value={form.full_name}
              onChange={(e) => setField('full_name', e.target.value)}
              required
              error={
                fieldErrors.full_name !== undefined
                  ? { message: fieldErrors.full_name }
                  : undefined
              }
            />
            <Input
              label="Password"
              variant="password"
              value={form.password}
              onChange={(e) => setField('password', e.target.value)}
              required
              helper="Minimum 12 characters"
              error={
                fieldErrors.password !== undefined
                  ? { message: fieldErrors.password }
                  : undefined
              }
            />
            <Input
              label="Confirm Password"
              variant="password"
              value={form.confirm_password}
              onChange={(e) => setField('confirm_password', e.target.value)}
              required
              error={
                fieldErrors.confirm_password !== undefined
                  ? { message: fieldErrors.confirm_password }
                  : undefined
              }
            />
            <div>
              <Checkbox
                label="Grant super-admin privileges"
                checked={form.is_super_admin}
                onChange={(e) => setField('is_super_admin', e.target.checked)}
              />
              <p className={styles.checkboxHelper}>
                Super admins can manage other admins and access the
                platform-admin console.
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className={styles.footer}>
            <Button
              type="submit"
              variant="primary"
              icon={ShieldCheck}
              loading={create.isPending}
            >
              {create.isPending ? 'Creating...' : 'Create Admin'}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
